import React, { useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Vote, TrendingUp, DollarSign, BarChart3, ArrowRight, CheckCircle, ShieldCheck, Sparkles } from 'lucide-react';
import StaticResourceCard from '../components/StaticResourceCard';
import { STATIC_FEATURED_RESOURCES } from '../data/staticFeaturedResources';

const CryptoRecoveryMatrixCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let width = 0;
    let height = 0;
    let mouse = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 };

    const handleMouseMove = (e) => {
      // Only track mouse hover on devices that support mouse pointers (>=768px) to prevent jumpy taps on mobile
      if (window.innerWidth < 768) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      mouse.targetX = (e.clientX - rect.left) / rect.width;
      mouse.targetY = (e.clientY - rect.top) / rect.height;
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    const resize = () => {
      if (!canvas) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      if (!width || !height) {
        width = canvas.parentElement ? canvas.parentElement.offsetWidth : window.innerWidth;
        height = canvas.parentElement ? canvas.parentElement.offsetHeight : window.innerHeight;
      }
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      resizeObserver = new ResizeObserver(() => {
        resize();
      });
      resizeObserver.observe(canvas.parentElement);
    }

    // Cryptographic Token & Data Particles
    const tokenSymbols = ['RFND', '0x', '✓', 'DAO', '⚡', '♦', 'POL'];
    const particles = Array.from({ length: 30 }, () => ({
      x: Math.random() * (width || 1200),
      y: Math.random() * (height || 800),
      text: tokenSymbols[Math.floor(Math.random() * tokenSymbols.length)],
      size: Math.random() * 8 + 8,
      speedY: Math.random() * 0.35 + 0.15,
      speedX: (Math.random() - 0.5) * 0.15,
      alpha: Math.random() * 0.35 + 0.12,
      pulsePhase: Math.random() * Math.PI * 2,
      pulseSpeed: Math.random() * 0.02 + 0.01
    }));

    // Blockchain conduits / energy streams connecting across screen
    const conduits = [
      { startX: 0.06, startY: 0.22, cpX: 0.22, cpY: 0.20, color: '#38bdf8' },
      { startX: 0.05, startY: 0.78, cpX: 0.24, cpY: 0.72, color: '#60a5fa' },
      { startX: 0.94, startY: 0.22, cpX: 0.78, cpY: 0.20, color: '#818cf8' },
      { startX: 0.95, startY: 0.78, cpX: 0.76, cpY: 0.72, color: '#38bdf8' },
      { startX: 0.50, startY: 0.96, cpX: 0.50, cpY: 0.80, color: '#60a5fa' }
    ];

    // Energy packet pulses traveling along conduits
    const packets = conduits.map((c, i) => ({
      conduitIndex: i,
      progress: (i * 0.22) % 1,
      speed: 0.003 + Math.random() * 0.002,
      size: 3.5 + Math.random() * 1.5
    }));

    let time = 0;

    const draw = () => {
      time += 0.015;
      
      const isMobile = width < 640;

      // Parallax smooth interpolation
      if (!isMobile) {
        mouse.x += (mouse.targetX - mouse.x) * 0.05;
        mouse.y += (mouse.targetY - mouse.y) * 0.05;
      } else {
        mouse.x = 0.5;
        mouse.y = 0.5;
      }

      ctx.clearRect(0, 0, width, height);

      // Parallax intensity: subtle on desktop (0.04), zero on mobile to keep hologram rock-solid centered
      const parallaxFactor = isMobile ? 0 : 0.04;
      const centerX = width * (0.5 + (mouse.x - 0.5) * parallaxFactor);
      
      // Dynamic center Y: on desktop 0.48, on mobile 0.46 for perfect visual alignment with the grand title & subtitle
      const centerY = height * (isMobile ? 0.46 : 0.48 + (mouse.y - 0.5) * parallaxFactor);

      // PROPORTIONAL SCALE FACTOR:
      // Computes max safe radius so the outermost ring and glow never exceed canvas bounds (zero cut-off on any screen)
      const maxAllowedRadius = Math.min(width * 0.38, height * 0.35, 250);
      const scale = Math.max(0.40, maxAllowedRadius / 240);

      // --- 1. Background Cryptographic Hex Lattice ---
      const hexRadius = Math.max(26, 40 * scale);
      const hexWidth = hexRadius * Math.sqrt(3);
      const hexHeight = hexRadius * 1.5;
      const maxLatticeDist = Math.min(Math.max(width, height) * 0.65, 540 * scale);
      ctx.lineWidth = Math.max(0.5, 0.6 * scale);

      for (let y = -hexRadius; y < height + hexRadius * 2; y += hexHeight) {
        for (let x = -hexWidth; x < width + hexWidth * 2; x += hexWidth) {
          const offsetX = (Math.floor(y / hexHeight) % 2 === 1) ? hexWidth / 2 : 0;
          const hx = x + offsetX;
          const hy = y;

          // Distance from center
          const dx = hx - centerX;
          const dy = hy - centerY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Smooth edge boundary fade to eliminate any cutoff
          const edgeDistX = Math.min(hx, width - hx);
          const edgeDistY = Math.min(hy, height - hy);
          const edgeFade = Math.max(0, Math.min(1, edgeDistX / 35, edgeDistY / 35));

          if (dist > 50 * scale && dist < maxLatticeDist && edgeFade > 0) {
            const wave = Math.sin(dist * 0.015 - time * 1.5);
            const alpha = Math.max(0, (1 - dist / maxLatticeDist) * 0.11 * (0.6 + 0.4 * wave)) * edgeFade;
            
            if (alpha > 0.004) {
              ctx.beginPath();
              for (let a = 0; a < 6; a++) {
                const angle = (Math.PI / 3) * a - Math.PI / 6;
                const px = hx + (hexRadius * 0.88) * Math.cos(angle);
                const py = hy + (hexRadius * 0.88) * Math.sin(angle);
                if (a === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
              }
              ctx.closePath();
              ctx.strokeStyle = `rgba(96, 165, 250, ${alpha})`;
              ctx.stroke();

              // Occasional glowing active verification node
              if (Math.sin(hx * 11 + hy * 7 + time) > 0.88 && dist < maxLatticeDist * 0.75) {
                ctx.beginPath();
                ctx.arc(hx, hy, Math.max(1.2, 1.8 * scale), 0, Math.PI * 2);
                ctx.fillStyle = `rgba(56, 189, 248, ${alpha * 2.5})`;
                ctx.fill();
              }
            }
          }
        }
      }

      // --- 2. Central DAO Verification Shield Hologram ---
      ctx.save();
      ctx.translate(centerX, centerY);

      // Core Ambient Radial Glow (contained within canvas bounds)
      const glowRadius = 280 * scale;
      const shieldGlow = ctx.createRadialGradient(0, 0, 8 * scale, 0, 0, glowRadius);
      shieldGlow.addColorStop(0, 'rgba(37, 99, 235, 0.28)');
      shieldGlow.addColorStop(0.4, 'rgba(30, 58, 138, 0.15)');
      shieldGlow.addColorStop(0.8, 'rgba(15, 23, 42, 0.04)');
      shieldGlow.addColorStop(1, 'rgba(10, 22, 40, 0)');
      ctx.fillStyle = shieldGlow;
      ctx.beginPath();
      ctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
      ctx.fill();

      // Outer Telemetry Ring
      const rOuter = 230 * scale;
      ctx.beginPath();
      ctx.arc(0, 0, rOuter, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(59, 130, 246, 0.18)';
      ctx.lineWidth = Math.max(1, 1.2 * scale);
      ctx.stroke();

      // Rotating Cryptographic Ring with Hash Ticks
      const rCrypto = 190 * scale;
      ctx.save();
      ctx.rotate(time * 0.2);
      ctx.beginPath();
      ctx.arc(0, 0, rCrypto, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(96, 165, 250, 0.25)';
      ctx.setLineDash([6 * scale, 12 * scale, 16 * scale, 12 * scale]);
      ctx.lineWidth = Math.max(1, 1.3 * scale);
      ctx.stroke();

      // Cardinal telemetry brackets
      const rBrackets = 205 * scale;
      for (let i = 0; i < 4; i++) {
        ctx.save();
        ctx.rotate((Math.PI / 2) * i);
        ctx.beginPath();
        ctx.arc(0, 0, rBrackets, -0.15, 0.15);
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.55)';
        ctx.lineWidth = Math.max(1.5, 2 * scale);
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();

      // Counter-rotating Inner Token Ring
      const rInner = 140 * scale;
      ctx.save();
      ctx.rotate(-time * 0.15);
      ctx.beginPath();
      ctx.arc(0, 0, rInner, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(147, 197, 253, 0.18)';
      ctx.setLineDash([10 * scale, 20 * scale]);
      ctx.lineWidth = Math.max(1, 1.1 * scale);
      ctx.stroke();
      ctx.restore();

      // Central Shield Crest Outline
      const sw = 80 * scale;
      const sh = 100 * scale;
      ctx.beginPath();
      ctx.moveTo(0, -sh);
      ctx.lineTo(sw, -sh * 0.45);
      ctx.lineTo(sw * 0.75, sh * 0.45);
      ctx.lineTo(0, sh);
      ctx.lineTo(-sw * 0.75, sh * 0.45);
      ctx.lineTo(-sw, -sh * 0.45);
      ctx.closePath();
      ctx.strokeStyle = 'rgba(96, 165, 250, 0.32)';
      ctx.lineWidth = Math.max(1.2, 1.6 * scale);
      ctx.stroke();

      // Secondary Inner Shield
      const isw = sw * 0.78;
      const ish = sh * 0.78;
      ctx.beginPath();
      ctx.moveTo(0, -ish);
      ctx.lineTo(isw, -ish * 0.45);
      ctx.lineTo(isw * 0.75, ish * 0.45);
      ctx.lineTo(0, ish);
      ctx.lineTo(-isw * 0.75, ish * 0.45);
      ctx.lineTo(-isw, -ish * 0.45);
      ctx.closePath();
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.18)';
      ctx.lineWidth = Math.max(0.8, 1.1 * scale);
      ctx.stroke();

      // Vertical Scanning Laser Beam through Shield
      const scanY = Math.sin(time * 1.8) * sh;
      const scanGrad = ctx.createLinearGradient(-sw * 0.9, scanY, sw * 0.9, scanY);
      scanGrad.addColorStop(0, 'rgba(56, 189, 248, 0)');
      scanGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.65)');
      scanGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
      ctx.beginPath();
      ctx.moveTo(-sw * 0.8, scanY);
      ctx.lineTo(sw * 0.8, scanY);
      ctx.strokeStyle = scanGrad;
      ctx.lineWidth = Math.max(1.2, 1.8 * scale);
      ctx.stroke();

      ctx.restore();

      // --- 3. Flowing Blockchain Transaction Conduits ---
      conduits.forEach((c) => {
        const sx = width * c.startX;
        const sy = height * c.startY;
        const ex = centerX;
        const ey = centerY;
        const cpx = width * c.cpX + (mouse.x - 0.5) * 20;
        const cpy = height * c.cpY + (mouse.y - 0.5) * 20;

        // Static subtle conduit line
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.quadraticCurveTo(cpx, cpy, ex, ey);
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.12)';
        ctx.lineWidth = Math.max(0.7, 1 * scale);
        ctx.setLineDash([4 * scale, 6 * scale]);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // --- 4. Animated Transaction / Recovery Energy Packets ---
      packets.forEach((p) => {
        p.progress += p.speed;
        if (p.progress > 1) p.progress = 0;

        const c = conduits[p.conduitIndex];
        const sx = width * c.startX;
        const sy = height * c.startY;
        const ex = centerX;
        const ey = centerY;
        const cpx = width * c.cpX + (mouse.x - 0.5) * 20;
        const cpy = height * c.cpY + (mouse.y - 0.5) * 20;

        // Quadratic Bezier interpolation
        const t = p.progress;
        const invT = 1 - t;
        const px = invT * invT * sx + 2 * invT * t * cpx + t * t * ex;
        const py = invT * invT * sy + 2 * invT * t * cpy + t * t * ey;

        const packetSize = p.size * scale;

        // Glowing packet orb
        ctx.save();
        ctx.beginPath();
        ctx.arc(px, py, Math.max(2, packetSize), 0, Math.PI * 2);
        ctx.fillStyle = c.color;
        ctx.shadowColor = c.color;
        ctx.shadowBlur = Math.max(6, 10 * scale);
        ctx.fill();

        // Trailing glow tail
        const tailT = Math.max(0, t - 0.05);
        const invTailT = 1 - tailT;
        const tailX = invTailT * invTailT * sx + 2 * invTailT * tailT * cpx + tailT * tailT * ex;
        const tailY = invTailT * invTailT * sy + 2 * invTailT * tailT * cpy + tailT * tailT * ey;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(px, py);
        ctx.strokeStyle = c.color;
        ctx.lineWidth = Math.max(1, packetSize * 0.7);
        ctx.stroke();
        ctx.restore();
      });

      // --- 5. Rising Cryptographic Token Particles ---
      const activeParticleCount = isMobile ? 16 : 28;
      for (let i = 0; i < activeParticleCount; i++) {
        const pt = particles[i];
        pt.y -= pt.speedY;
        pt.x += pt.speedX;
        pt.pulsePhase += pt.pulseSpeed;
        if (pt.y < 0) {
          pt.y = height + 15;
          pt.x = Math.random() * width;
        }

        const alpha = pt.alpha * (0.6 + 0.4 * Math.sin(pt.pulsePhase));
        ctx.save();
        ctx.font = `600 ${Math.max(8, pt.size * scale)}px monospace`;
        ctx.fillStyle = `rgba(147, 197, 253, ${alpha})`;
        ctx.shadowColor = 'rgba(56, 189, 248, 0.5)';
        ctx.shadowBlur = 4;
        ctx.fillText(pt.text, pt.x, pt.y);
        ctx.restore();
      }

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.95 }}
    />
  );
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const ref = params.get('ref');
      if (ref) localStorage.setItem('landingReferralCode', ref);
    } catch {}
  }, [location.search]);

  const features = [
    {
      icon: DollarSign,
      title: 'Secure Distribution',
      description: 'Help ensure recovered funds are securely distributed to verified victims through a transparent, structured recovery process.'
    },
    {
      icon: BarChart3,
      title: 'Victim & Recovery Stats',
      description: 'View key statistics on verified victims, recovered funds, and active refund programs.'
    },
    {
      icon: TrendingUp,
      title: 'Recovery Program Tracker',
      description: 'Track active refund programs, recovery milestones, and the distribution of recovered funds.'
    },
    {
      icon: Vote,
      title: 'Transparent Voting',
      description: 'Participate in transparent voting to provide feedback on recovery campaigns and fund distribution, helping improve future efforts and promote accountability.'
    }
  ];

  const highlights = [
    'On-chain Proof-of-Loss tokens (RFND)',
    'Decentralized governance & voting',
    'Government-grade verification',
    'Transparent fund distribution'
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* 
        ============================================================
        SINGLE VIEWPORT HERO SECTION WITH BLOCKCHAIN RECOVERY MATRIX
        ============================================================
      */}
      <section
        className="relative w-full min-h-[calc(100vh-5rem)] py-4 sm:py-6 lg:py-8 flex flex-col justify-center items-center overflow-hidden px-4 sm:px-6"
        style={{ background: 'linear-gradient(135deg, #07111e 0%, #0a1628 40%, #0d1e38 75%, #07111e 100%)' }}
      >
        <CryptoRecoveryMatrixCanvas />

        {/* Floating Concept Recovery Badges (Visible on ultra-wide screens to prevent central overlap) */}
        <div className="hidden 2xl:block absolute left-8 2xl:left-14 top-1/4 z-10 pointer-events-none">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-xl"
            style={{
              background: 'rgba(15, 23, 42, 0.65)',
              borderColor: 'rgba(56, 189, 248, 0.3)',
              boxShadow: '0 8px 24px rgba(56, 189, 248, 0.15)'
            }}
          >
            <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
            <div className="text-left">
              <div className="text-[11px] font-bold text-blue-300 tracking-wide uppercase">Proof-of-Loss</div>
              <div className="text-xs font-semibold text-white">Loss Verified ✓</div>
            </div>
          </motion.div>
        </div>

        <div className="hidden 2xl:block absolute left-10 2xl:left-16 bottom-1/4 z-10 pointer-events-none">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-xl"
            style={{
              background: 'rgba(15, 23, 42, 0.65)',
              borderColor: 'rgba(96, 165, 250, 0.3)',
              boxShadow: '0 8px 24px rgba(37, 99, 235, 0.15)'
            }}
          >
            <span className="flex h-2.5 w-2.5 rounded-full bg-blue-400 animate-ping" />
            <div className="text-left">
              <div className="text-[11px] font-bold text-blue-300 tracking-wide uppercase">Smart Contract</div>
              <div className="text-xs font-semibold text-white">RFND Mint Protocol</div>
            </div>
          </motion.div>
        </div>

        <div className="hidden 2xl:block absolute right-8 2xl:right-14 top-1/4 z-10 pointer-events-none">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-xl"
            style={{
              background: 'rgba(15, 23, 42, 0.65)',
              borderColor: 'rgba(129, 140, 248, 0.3)',
              boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15)'
            }}
          >
            <span className="flex h-2.5 w-2.5 rounded-full bg-indigo-400" />
            <div className="text-left">
              <div className="text-[11px] font-bold text-indigo-300 tracking-wide uppercase">DAO Governance</div>
              <div className="text-xs font-semibold text-white">Multi-Sig Approved</div>
            </div>
          </motion.div>
        </div>

        <div className="hidden 2xl:block absolute right-10 2xl:right-16 bottom-1/4 z-10 pointer-events-none">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 0.9 }}
            className="flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border backdrop-blur-md shadow-xl"
            style={{
              background: 'rgba(15, 23, 42, 0.65)',
              borderColor: 'rgba(56, 189, 248, 0.3)',
              boxShadow: '0 8px 24px rgba(56, 189, 248, 0.15)'
            }}
          >
            <span className="flex h-2.5 w-2.5 rounded-full bg-cyan-400 animate-pulse" />
            <div className="text-left">
              <div className="text-[11px] font-bold text-cyan-300 tracking-wide uppercase">Recovery Vault</div>
              <div className="text-xs font-semibold text-white">Secure Distribution</div>
            </div>
          </motion.div>
        </div>

        {/* Ambient radial glows */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2 max-w-[100vw]" style={{ background: 'radial-gradient(circle, rgba(29, 78, 216, 0.22) 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/3 right-1/4 w-[550px] h-[550px] rounded-full pointer-events-none translate-x-1/2 translate-y-1/2 max-w-[100vw]" style={{ background: 'radial-gradient(circle, rgba(37, 99, 235, 0.16) 0%, transparent 70%)' }} />

        {/* Centered Main Hero Content */}
        <div className="relative z-10 w-full max-w-5xl lg:max-w-6xl xl:max-w-7xl mx-auto text-center flex flex-col items-center justify-center space-y-3.5 sm:space-y-4 md:space-y-5 lg:space-y-5 my-auto py-2">
          
          {/* Glowing Protocol Badge */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 sm:gap-2.5 px-4 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-semibold shadow-lg backdrop-blur-md max-w-full"
            style={{
              background: 'rgba(30, 58, 138, 0.5)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              color: '#bfdbfe',
              boxShadow: '0 0 20px rgba(29, 78, 216, 0.3)'
            }}
          >
            <span className="relative flex h-2 sm:h-2.5 w-2 sm:w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex h-2 sm:h-2.5 w-2 sm:w-2.5 rounded-full bg-blue-400" />
            </span>
            <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400 inline shrink-0" />
            <span className="truncate">Decentralized Recovery Protocol · DAO Powered</span>
          </motion.div>

          {/* Grand Brand Title */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="w-full">
            <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-black text-white tracking-tight leading-none drop-shadow-2xl">
              Avera<span style={{
                background: 'linear-gradient(90deg, #60a5fa 0%, #93c5fd 40%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 40px rgba(96,165,250,0.5)'
              }}>dao</span>
            </h1>
          </motion.div>

          {/* Impact Subtitle */}
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-tight max-w-4xl lg:max-w-5xl px-2"
          >
            Driven by Truth.{' '}
            <span style={{
              background: 'linear-gradient(90deg, #60a5fa 0%, #93c5fd 60%, #dbeafe 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Returning What's Yours.
            </span>
          </motion.h2>

          {/* Clear Protocol Narrative */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100/90 leading-relaxed max-w-3xl lg:max-w-4xl xl:max-w-5xl font-normal px-2"
          >
            Averadao helps government agencies securely return cryptocurrency recovered from fraud, financial crimes, and illegal business practices to verified victims through on-chain Proof-of-Loss tokens (RFND).
          </motion.p>

          {/* Key Feature Highlight Chips */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 md:gap-3 max-w-4xl lg:max-w-5xl pt-0.5 px-2"
          >
            {highlights.map((h, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 rounded-xl text-xs sm:text-sm font-semibold text-blue-100 backdrop-blur-sm"
                style={{
                  background: 'rgba(30, 58, 138, 0.35)',
                  border: '1px solid rgba(96, 165, 250, 0.25)'
                }}
              >
                <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400 shrink-0" />
                <span>{h}</span>
              </div>
            ))}
          </motion.div>

          {/* Main Action CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 pt-1.5 w-full sm:w-auto px-4"
          >
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                const ref = localStorage.getItem('landingReferralCode');
                navigate(ref ? `/join-notice?ref=${encodeURIComponent(ref)}` : '/join-notice');
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 sm:px-9 py-3 sm:py-3.5 rounded-xl font-extrabold text-white text-sm sm:text-base shadow-xl cursor-pointer transition-all"
              style={{
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #1e40af 100%)',
                border: '1px solid rgba(147, 197, 253, 0.45)',
                boxShadow: '0 8px 30px rgba(29, 78, 216, 0.5)'
              }}
            >
              <span>Request a Refund</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="w-full sm:w-auto">
              <Link
                to="/contact"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 sm:px-9 py-3 sm:py-3.5 rounded-xl font-bold text-blue-100 text-sm sm:text-base transition-all"
                style={{
                  background: 'rgba(30, 58, 138, 0.4)',
                  border: '1px solid rgba(96, 165, 250, 0.35)',
                  boxShadow: '0 4px 15px rgba(10, 22, 40, 0.3)'
                }}
              >
                Talk to Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 
        ============================================================
        REST OF HOMEPAGE CONTENT (Scrolled below single viewport)
        ============================================================
      */}
      {/* Recovery Resources Section */}
      <section className="w-full overflow-x-hidden pt-12 pb-16 bg-gray-50">
        <div className="w-full min-w-0 mobile-padding">
          <div className="mb-10 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-2 text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight"
            >
              Recovery resources and guides
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mx-auto max-w-3xl text-base sm:text-lg text-gray-600"
            >
              Scam alerts, Averadao refund programs, and an overview of how we help eligible victims recover funds.
            </motion.p>
          </div>
          <div className="mx-auto grid max-w-6xl grid-cols-1 justify-items-center gap-8 md:grid-cols-3 md:justify-items-stretch md:gap-8">
            {STATIC_FEATURED_RESOURCES.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-24px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="w-full max-w-[380px] md:max-w-none"
              >
                <StaticResourceCard
                  to={item.path}
                  title={item.title}
                  description={item.description}
                  iconSrc={item.iconSrc}
                  iconAlt={item.iconAlt}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How Averadao Helps Section */}
      <section className="w-full pt-16 pb-24 bg-white relative z-0">
        <div className="w-full mobile-padding">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3"
            >
              How Averadao Helps
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg text-gray-600 max-w-3xl mx-auto"
            >
              Verify eligible victims, issue on-chain Proof-of-Loss tokens, and facilitate the secure distribution of recovered funds.
            </motion.p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center max-w-7xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card p-6 text-center hover:scale-105 transition-transform duration-300 w-full flex flex-col items-center justify-start h-full"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-900/25 shrink-0"
                    style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)' }}
                  >
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deep Navy CTA Section */}
      <section
        className="w-full py-20 lg:py-24 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a1628 0%, #0f172a 50%, #172554 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(29, 78, 216, 0.18) 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 right-1/3 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)' }} />
        </div>
        <div className="w-full mobile-padding max-w-4xl mx-auto text-center space-y-6 relative z-10">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
          >
            You Don't Have to Navigate This Alone
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base sm:text-lg text-blue-200/85 leading-relaxed max-w-2xl mx-auto space-y-3"
          >
            <p>If you've lost funds to a scam or need help understanding the recovery process, reach out to Averadao.</p>
            <p>Tell us what happened, ask your questions, and learn more about the options available to you.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl font-extrabold text-white text-base sm:text-lg transition-all duration-300 shadow-xl"
              style={{
                background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
                border: '1px solid rgba(147, 197, 253, 0.4)',
                boxShadow: '0 8px 25px rgba(29, 78, 216, 0.4)'
              }}
            >
              Talk to Averadao <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;